<template>
    <div
        :class="{
            'desktop-layout-form-view': $q.platform.is.desktop
        }"
    >
        <q-form @submit="inEditMode ? update() : create()">
            <q-input
                v-model="productCategory.name"
                @clear="productCategory.name = ''"
                :label="$t('Name')"
                class="q-mb-sm q-pb-md"
                style="width: 100%"
                clearable
                outlined
                lazy-rules
                bg-color="white"
                color="accent"
            >
                <template v-slot:prepend>
                    <q-icon name="fas fa-tag" />
                </template>
            </q-input>
            <!-- action group -->
            <div class="row form__actions justify-between">
                <div class="q-col">
                    <q-btn
                        v-show="inEditMode"
                        @click="handleRemove()"
                        class="float-left q-px-xs q-mb-lg"
                        icon="fas fa-trash-alt"
                        color="negative"
                        outline
                    />
                    <q-space />
                </div>

                <div class="q-col">
                    <q-btn
                        @click="$router.go(-1)"
                        :label="$t('Cancel')"
                        class="q-mr-sm q-mb-lg bg-white"
                        text-color="primary"
                        outline
                    />
                    <q-btn
                        :label="$t('Save')"
                        class="q-mb-lg"
                        type="submit"
                        color="primary"
                        id="save-button"
                    />
                </div>
            </div>
        </q-form>
    </div>
</template>
<script>
import { mapActions } from 'pinia';
import { useSharedStore } from '@stores/shared.js';
import { useProductsStore } from '@stores/products.js';
import BreadcrumbsManager from '@mixins/BreadcrumbsManager.vue';

export default {
    name: 'ProductCategoryDetail',
    mixins: [BreadcrumbsManager],
    created() {
        this.setBreadcrumbs([
            {
                label: this.$t('Facilities'),
                route: {
                    name: 'facility_list'
                }
            },
            {
                label: this.$t('Global Settings'),
                route: {
                    name: 'settings'
                }
            },
            {
                label: this.$t('Product Categories'),
                route: {
                    name: 'product_categories'
                }
            },
            {
                label: this.inEditMode
                    ? history.state.name
                    : this.$t('Create Product Category')
            }
        ]);
        if (this.inEditMode) {
            this.productCategory.name = history.state.name;
            this.productCategory.id = history.state.id;
        }
    },
    computed: {
        /**
         * Check if the user is in edit mode
         * @returns {boolean} - true if the user is in edit mode and false otherwise
         */
        inEditMode() {
            return history.state.name !== undefined;
        }
    },
    data() {
        return {
            productCategory: {
                id: null,
                name: null
            }
        };
    },
    methods: {
        ...mapActions(useSharedStore, ['beginLoading', 'stopLoading']),
        ...mapActions(useProductsStore, [
            'createProductCategoryAction',
            'updateProductCategoryAction',
            'removeProductCategoryAction'
        ]),
        /**
         * Create a new product category.
         */
        async create() {
            // begin loading
            this.beginLoading(`${this.$t('Creating new product category')}...`);
            try {
                await this.createProductCategoryAction(this.productCategory);
                this.$router.go(-1);
                this.$notifySuccess(
                    this.$t(
                        'The product category has been created successfully.'
                    )
                );
            } catch (error) {
                this.handleApiErrors(error, 'create');
            } finally {
                // end loading
                this.stopLoading();
            }
        },
        /**
         * Update an existing product category.
         */
        async update() {
            // begin loading
            this.beginLoading(`${this.$t('Updating product category')}...`);
            try {
                await this.updateProductCategoryAction(this.productCategory);
                this.$router.go(-1);
                this.$notifySuccess(
                    this.$t(
                        'The product category has been updated successfully'
                    )
                );
            } catch (error) {
                this.handleApiErrors(error, 'update');
            } finally {
                // end loading
                this.stopLoading();
            }
        },
        /**
         * Remove an existing product category.
         */
        async handleRemove() {
            try {
                // begin loading
                this.beginLoading(`${this.$t('Removing product category')}...`);
                await this.removeProductCategoryAction(this.productCategory);
                this.$router.go(-1);
                this.$notifySuccess(
                    this.$t(
                        'The product category has been removed successfully.'
                    )
                );
            } catch (error) {
                this.$notifyError(
                    this.$t(
                        'Failed to remove product category. Please try again later.'
                    ),
                    error
                );
            } finally {
                // end loading
                this.stopLoading();
            }
        },
        /**
         * Display the relevant error message based on the API response.
         * @param {Object} e - The error object returned from the API.
         * @param {String} action - The action that was being performed when the error occurred.
         */
        handleApiErrors(e, action) {
            const error =
                e?.response?.data && typeof e.response.data === 'object'
                    ? e.response.data
                    : {};
            if (
                'name' in error &&
                error['name'].length > 0 &&
                error['name'][0] ==
                    'product category with this name already exists.'
            ) {
                this.$notifyError(
                    this.$t(
                        'Product category with this name already exists. Please use a different name.'
                    ),
                    error
                );
            } else {
                if (action === 'create') {
                    this.$notifyError(
                        this.$t(
                            'Failed to create product category. Please try again later.'
                        ),
                        error
                    );
                } else {
                    this.$notifyError(
                        this.$t(
                            'Failed to update product category. Please try again later.'
                        ),
                        error
                    );
                }
            }
        }
    }
};
</script>
